import { AddItemsProduct, IsolatedCartType, getBuyNowKey } from '@teko/cart/v2';
import { DiscoverySearchProductResponse } from '@teko/discovery/v2';
import { logger } from '@teko/log';
import { ProductCardProps } from '@teko/types/ecommerce';
import { cleanObject, isEmptyObject } from '@teko/utils';
import TerraKit from '@terra-js/terra-kit';
import { CartType } from '@terra-js/terra-kit/dist/common/CommonKitModule';
import {
  CUSTOM_HAMPER_LOCAL_STORAGE_APP_KEY,
  CUSTOM_HAMPER_LOCAL_STORAGE_KEY,
} from 'constants/index';
import {
  buildHamperNote,
  getHamperDataLocalConfigs,
  observeCheckoutHamperResult,
} from 'helpers';
import { scrollSmoothToIdWithOffset } from 'helpers/page-actions';
import { useSearch } from 'hooks';
import { t } from 'i18n';
import { useCartV2 } from 'modules/cart/CartContextV2';
import { useRouter } from 'next/router';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { HamperConfigType, IHamperData, IHamperItemData } from 'types';
import { useTenantConfig } from './GlobalDataContext';
import { useServices } from './ServiceContext';
import { useToast } from './ToastContext';

export interface ProductCardPropsHamper extends ProductCardProps {
  quantity: number;
}

interface AvailableHamper {
  imageUrl: string;
  quantity: number;
  name: string;
  sku: string;
  price: number;
}

export interface HamperContext {
  hamperData: IHamperData;
  currentHamperConfig: HamperConfigType;
  changeActiveConfig: (activeIndex: number) => void;
  updateCurrentConfig: (
    newConfig: HamperConfigType,
    targetIndex?: number,
  ) => void;
  onUpdateConfig: (product: ProductCardProps) => Promise<void>;
  hamperDetailProducts: DiscoverySearchProductResponse<ProductCardProps>;
  loading: boolean;
  increaseHamperItemQuantity: (hamperItemKey: string, sku: string) => void;
  decreaseHamperItemQuantity: (hamperItemKey: string, sku: string) => void;
  removeHamperItem: (hamperItemKey: string, sku: string) => void;
  setHamperItemQuantity: (
    hamperItemKey: string,
    quantity: number,
    sku: string,
  ) => void;

  increaseCurrentHamperQuantity: (targetIndex?: number) => void;
  decreaseCurrentHamperQuantity: (targetIndex?: number) => void;
  setCurrentHamperQuantity: (quantity: number, targetIndex?: number) => void;
  addHamper: (sku: string) => void;
  minusHamper: (sku: string) => void;
  removeHamper: (sku: string) => void;
  onChangeQuantityHamper: (sku: string, quantity: number) => void;
  productHampers: ProductCardProps[];
  setProductHampers: (productHampers: ProductCardProps[]) => void;
  priceSummary: {
    index: number;
    price: number;
    products: ProductCardPropsHamper[];
    quantity: number;
  }[];
  priceAvailableHamper: number;
  allPriceSummary: number;
  handleBuyNowHamper: () => Promise<void>;
  availableHamper: AvailableHamper[];
}

const initialState: HamperContext = {
  hamperData: {
    configs: [
      { quantity: 0 },
      { quantity: 0 },
      { quantity: 0 },
      { quantity: 0 },
      { quantity: 0 },
    ],
    activeIndex: 0,
    hampers: [],
  },
  currentHamperConfig: { quantity: 1 },
  changeActiveConfig: () => {},
  updateCurrentConfig: () => {},
  onUpdateConfig: () => Promise.resolve(),
  increaseHamperItemQuantity: () => {},
  decreaseHamperItemQuantity: () => {},
  removeHamperItem: () => {},
  setHamperItemQuantity: () => {},
  increaseCurrentHamperQuantity: () => {},
  decreaseCurrentHamperQuantity: () => {},
  setCurrentHamperQuantity: () => {},
  hamperDetailProducts: {
    products: [],
    filters: {},
    pagination: { totalItems: 0, totalPages: 0 },
  },
  loading: false,
  addHamper: () => {},
  minusHamper: () => {},
  removeHamper: () => {},
  onChangeQuantityHamper: () => {},
  productHampers: [],
  setProductHampers: () => {},
  priceSummary: [],
  priceAvailableHamper: 0,
  allPriceSummary: 0,
  handleBuyNowHamper: () => Promise.resolve(),
  availableHamper: [],
};

const HamperContext = createContext<HamperContext>(initialState);

interface HamperProviderProps {
  children: React.ReactNode;
}
export const HamperProvider = ({ children }: HamperProviderProps) => {
  const router = useRouter();
  const { bomMapping, isMobile } = useTenantConfig();
  const { discoveryServiceV2 } = useServices();
  const { onAddMultipleItems } = useCartV2();
  const toast = useToast();

  const [loading, setLoading] = useState(false);
  const [hamperData, setHamperData] = useState<IHamperData>(
    getHamperDataLocalConfigs(),
  );
  const [productHampers, setProductHampers] = useState<ProductCardProps[]>([]);

  const currentHamperConfig = useMemo(() => {
    return hamperData.configs[hamperData.activeIndex];
  }, [hamperData]);

  // get all skus from current active config
  const currentConfigSkus = useMemo(() => {
    const skus: string[] = [];
    hamperData.configs.forEach((hamperConfig) => {
      Object.keys(hamperConfig).forEach((configKey) => {
        const listProduct = hamperConfig[configKey];
        if (Array.isArray(listProduct)) {
          listProduct.forEach((p) => {
            if (!!p.sku && !skus.includes(p.sku)) {
              skus.push(p.sku);
            }
          });
        }
      });
    });

    return skus;
  }, [hamperData]);

  // update all local storage config when current config changes
  useEffect(() => {
    const hamperKey = TerraKit.isAvailable()
      ? CUSTOM_HAMPER_LOCAL_STORAGE_APP_KEY
      : CUSTOM_HAMPER_LOCAL_STORAGE_KEY;
    localStorage.setItem(hamperKey, JSON.stringify(hamperData));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(hamperData)]);

  // trigger update quantity of custom hamper -> when user selects material, set quantity of custom hamper = 1
  useEffect(() => {
    const currentHamperHasConfig = Object.values(
      hamperData.configs[hamperData.activeIndex],
    ).some((config) => Array.isArray(config) && config.length > 0);
    if (
      currentHamperHasConfig &&
      !hamperData.configs[hamperData.activeIndex]?.quantity
    ) {
      setHamperData({
        ...hamperData,
        configs: hamperData.configs.map((config, index) => {
          // check if config is the active config
          if (hamperData.activeIndex === index) {
            return {
              ...config,
              quantity: 1,
            };
          }
          return config;
        }),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(hamperData.configs[hamperData.activeIndex])]);

  const changeActiveConfig = (activeIndex: number) => {
    setHamperData({ ...hamperData, activeIndex });
  };

  const updateCurrentConfig = (
    newConfig: HamperConfigType,
    targetIndex?: number,
  ) => {
    const currentIndex =
      targetIndex !== undefined ? targetIndex : hamperData.activeIndex;

    setHamperData({
      ...hamperData,
      configs: hamperData.configs.map((config, index) => {
        // check if config is the active config
        if (index === currentIndex) {
          // replace with new config
          return {
            ...newConfig,
            quantity: isEmptyObject(newConfig) ? 0 : config.quantity || 0,
          };
        }
        return config;
      }),
      activeIndex: currentIndex,
    });
  };

  const onUpdateConfig = async (product: ProductCardProps) => {
    const targetIndex = hamperData.configs.findIndex(
      (item) => (item.quantity as number) <= 0,
    );
    if (targetIndex === -1) {
      toast?.error?.({
        message: t('Mỗi giỏ hàng chỉ được chọn 5 giỏ quà tự chọn!'),
        title: undefined,
      });
      return;
    }
    scrollSmoothToIdWithOffset('hamper-item-block', isMobile ? 55 : 90);
    if (
      !product.materials?.length ||
      !bomMapping ||
      isEmptyObject(bomMapping)
    ) {
      return;
    }

    try {
      setLoading(true);
      // get all material skus of current product
      const materialSkus = product.materials.map((material) => material.sku);

      // call api search-skus-v2 to get materials categories for mapping
      const data = await discoveryServiceV2.search({
        pageSize: materialSkus.length,
        filter: { skus: materialSkus },
      });

      const materialProducts = data.products;

      const bomMappingKeys =
        !!bomMapping && !isEmptyObject(bomMapping)
          ? Object.keys(bomMapping)
          : [];
      const visited = new Map<string, boolean>();

      const newConfig = bomMappingKeys.reduce((config, currentKey) => {
        // Get defined hamper mapping categories from tenant config
        const currentMappingCategories = bomMapping[currentKey].categories;
        const productsByKey: IHamperItemData[] = [];

        materialProducts?.forEach((materialProduct) => {
          // Check if any categories from materialProducts included in current key mapping categories
          const matchedCategory = materialProduct.categories?.find(
            (category) => {
              return currentMappingCategories.find((mappingCategory) => {
                return mappingCategory === category.code;
              });
            },
          );
          // // if exists, add material sku & quantity to new config
          if (!!matchedCategory && !!materialProduct.sku) {
            // find material quantity in product's materials by sku
            const matchedMaterial = product.materials?.find(
              (material) => material.sku === materialProduct.sku,
            );
            if (!!matchedMaterial && !visited.has(matchedMaterial.sku)) {
              productsByKey.push({
                sku: materialProduct.sku,
                quantity: matchedMaterial.quantity,
              });
              visited.set(matchedMaterial.sku, true);
            }
          }
        });
        config[currentKey] = productsByKey;
        return config;
      }, {} as HamperConfigType);

      if (!isEmptyObject(newConfig)) {
        updateCurrentConfig(
          newConfig,
          targetIndex !== -1 ? targetIndex : undefined,
        );
        if (targetIndex !== -1) {
          toast?.success?.({
            message: `${t('Xem chi tiết và chỉnh sửa giỏ quà tự chọn')} ${
              targetIndex + 1
            }`,
            title: undefined,
          });
        }
      }
    } catch (error) {
      logger.error(error);
    } finally {
      setLoading(false);
    }
  };

  // call api search-skus-v2 to get all current active config skus detail info
  const { result: hamperDetailProducts, isLoading } = useSearch(
    cleanObject(
      !!currentConfigSkus.length
        ? {
            pageSize: currentConfigSkus.length,
            filter: {
              skus: currentConfigSkus,
            },
          }
        : {},
    ),
  );

  const increaseHamperItemQuantity = (hamperItemKey: string, sku: string) => {
    const configs = hamperData.configs.map((config, index) => {
      // check if config is the active config
      if (hamperData.activeIndex !== index) return config;

      const currentConfigHamperItem = (config[hamperItemKey] ||
        []) as IHamperItemData[];
      const isNewSkuAdded =
        currentConfigHamperItem.length &&
        !currentConfigHamperItem.find((h) => h.sku === sku);

      // Add new key if not exists or add multiple items
      if (!currentConfigHamperItem.length || isNewSkuAdded) {
        return {
          ...config,
          [hamperItemKey]: [...currentConfigHamperItem, { sku, quantity: 1 }],
        };
      }

      return {
        ...config,
        [hamperItemKey]: currentConfigHamperItem.map((h) => {
          if (h.sku === sku) {
            return { ...h, quantity: h.quantity + 1 };
          }
          return h;
        }),
      };
    });

    setHamperData({ ...hamperData, configs });
  };

  const decreaseHamperItemQuantity = (hamperItemKey: string, sku: string) => {
    setHamperData({
      ...hamperData,
      configs: hamperData.configs.map((config, index) => {
        // check if config is the active config
        if (hamperData.activeIndex === index) {
          const currentConfigHamperItem = config[
            hamperItemKey
          ] as IHamperItemData[];
          return {
            ...config,
            [hamperItemKey]: currentConfigHamperItem.map((h) => {
              if (h.sku === sku) {
                return { ...h, quantity: h.quantity - 1 };
              }
              return h;
            }),
          };
        }
        return config;
      }),
    });
  };

  const removeHamperItem = (hamperItemKey: string, sku: string) => {
    setHamperData({
      ...hamperData,
      configs: hamperData.configs.map((config, index) => {
        // check if config is the active config
        if (hamperData.activeIndex === index) {
          const { [hamperItemKey]: _, quantity, ...remainingConfig } = config;
          const selectedValue = (
            config[hamperItemKey] as IHamperItemData[]
          ).filter((h) => h.sku !== sku);
          // check if remaining config is empty (has no material products) after remove item
          const isEmptyConfig =
            (Object.values(remainingConfig) as IHamperItemData[][]).every(
              (item: IHamperItemData[]) => item.length === 0,
            ) && !selectedValue.length;
          return {
            ...remainingConfig,
            [hamperItemKey]: selectedValue,
            quantity: isEmptyConfig ? 0 : quantity, // set quantity = 0 if config is empty (has no materials)
          };
        }
        return config;
      }),
    });
  };

  const setHamperItemQuantity = (
    hamperItemKey: string,
    quantity: number,
    sku: string,
  ) => {
    // stop when input quantity is 0
    if (!quantity) {
      return;
    }
    setHamperData({
      ...hamperData,
      configs: hamperData.configs.map((config, index) => {
        // check if config is the active config
        if (hamperData.activeIndex === index) {
          const currentConfigHamperItem = config[
            hamperItemKey
          ] as IHamperItemData[];
          return {
            ...config,
            [hamperItemKey]: currentConfigHamperItem.map((h) => {
              if (h.sku === sku) {
                return { ...h, quantity: quantity };
              }
              return h;
            }),
          };
        }
        return config;
      }),
    });
  };

  const increaseCurrentHamperQuantity = (targetIndex?: number) => {
    const currentIndex =
      targetIndex !== undefined ? targetIndex : hamperData.activeIndex;
    setHamperData({
      ...hamperData,
      configs: hamperData.configs.map((config, index) => {
        // check if config is the active config
        if (index === currentIndex) {
          return {
            ...config,
            quantity: (config.quantity as number) + 1,
          };
        }
        return config;
      }),
    });
  };

  const decreaseCurrentHamperQuantity = (targetIndex?: number) => {
    const currentIndex =
      targetIndex !== undefined ? targetIndex : hamperData.activeIndex;

    setHamperData({
      ...hamperData,
      configs: hamperData.configs.map((config, index) => {
        // check if config is the active config
        if (index === currentIndex) {
          const quantity = (config.quantity as number) - 1;
          let data = { ...config };
          delete data.quantity; // only get configs
          // if quantity less than or equal 0, reset all configs to empty array
          if (quantity <= 0) {
            data = Object.keys(data).reduce((obj: HamperConfigType, key) => {
              obj[key] = [];
              return obj;
            }, {});
          }
          return {
            ...data,
            quantity,
          };
        }
        return config;
      }),
    });
  };

  const setCurrentHamperQuantity = (quantity: number, targetIndex?: number) => {
    const currentIndex =
      targetIndex !== undefined ? targetIndex : hamperData.activeIndex;
    // stop when input quantity is 0
    if (!quantity) {
      return;
    }
    setHamperData({
      ...hamperData,
      configs: hamperData.configs.map((config, index) => {
        // check if config is the active config
        if (index === currentIndex) {
          return {
            ...config,
            quantity,
          };
        }
        return config;
      }),
    });
  };

  const addHamper = (sku: string) => {
    const currentHamperIndex = hamperData?.hampers?.findIndex(
      (h) => h.sku === sku,
    );
    const newHampers =
      currentHamperIndex === -1
        ? [...(hamperData?.hampers || []), { sku, quantity: 1 }]
        : hamperData?.hampers?.map((h) => {
            if (h.sku === sku) {
              return { ...h, quantity: (h.quantity || 0) + 1 };
            }
            return h;
          });
    setHamperData({ ...hamperData, hampers: newHampers });
  };

  const minusHamper = (sku: string) => {
    const currentHamperIndex = hamperData?.hampers?.findIndex(
      (h) => h.sku === sku,
    );
    if (currentHamperIndex === -1) {
      return;
    }
    const newHampers = hamperData?.hampers?.map((h) => {
      if (h.sku === sku) {
        return { ...h, quantity: (h.quantity || 0) - 1 };
      }
      return h;
    });
    setHamperData({ ...hamperData, hampers: newHampers });
  };

  const removeHamper = (sku: string) => {
    const newHampers = hamperData?.hampers?.filter((h) => {
      return h.sku !== sku;
    });
    setHamperData({ ...hamperData, hampers: newHampers });
  };

  const onChangeQuantityHamper = (sku: string, quantity: number) => {
    if (!quantity) {
      return;
    }
    const currentHamperIndex = hamperData?.hampers?.findIndex(
      (h) => h.sku === sku,
    );
    if (currentHamperIndex === -1) {
      return;
    }
    const newHampers =
      quantity !== 0
        ? hamperData?.hampers?.map((h) => {
            if (h.sku === sku) {
              return { ...h, quantity };
            }
            return h;
          })
        : hamperData?.hampers?.filter((h) => h.sku !== sku);
    setHamperData({ ...hamperData, hampers: newHampers });
  };

  // price summary
  const priceSummary = useMemo(() => {
    return (
      hamperData.configs
        .map((currentHamperData, index) => {
          const bomMappingKeys =
            !!bomMapping && !isEmptyObject(bomMapping)
              ? Object.keys(bomMapping)
              : [];
          const mapQuantity = new Map<string, number>();
          const products: ProductCardPropsHamper[] = [];

          bomMappingKeys?.forEach((key) => {
            const skus: string[] = [];
            const dataHamper =
              (currentHamperData[key] as IHamperItemData[]) || [];

            dataHamper.forEach((h: IHamperItemData) => {
              skus.push(h.sku);
              mapQuantity.set(h.sku, h.quantity);
            });
            hamperDetailProducts.products?.forEach((p) => {
              if (skus.includes(p.sku)) {
                products.push({
                  ...p,
                  quantity: mapQuantity.get(p.sku) || 0,
                });
              }
            });
          });

          return {
            price:
              products?.reduce((prev, cur) => {
                return (
                  prev + (cur.price?.latestPrice || 0) * (cur.quantity || 0)
                );
              }, 0) * ((currentHamperData?.['quantity'] as number) || 0),
            index,
            products,
            quantity: (currentHamperData?.['quantity'] as number) || 0,
          };
        })
        .filter((item) => item.price > 0) || []
    );
  }, [hamperData, bomMapping, hamperDetailProducts]);

  const allPriceSummary = useMemo(() => {
    return priceSummary.reduce((prev, cur) => prev + cur.price, 0);
  }, [priceSummary]);

  const priceAvailableHamper = useMemo(() => {
    return (hamperData.hampers || []).reduce((prev, cur) => {
      const { sku, quantity = 0 } = cur;
      const price =
        productHampers.find((p) => p.sku === sku)?.price?.latestPrice || 0;

      return prev + price * quantity;
    }, 0);
  }, [hamperData, productHampers]);

  const availableHamper: AvailableHamper[] =
    useMemo(() => {
      return (hamperData.hampers || [])
        .map((h) => {
          const product = productHampers.find((p) => p.sku === h.sku) || {
            imageUrl: '',
            name: '',
            price: {
              latestPrice: 0,
            },
          };

          return {
            imageUrl: product?.imageUrl,
            quantity: h.quantity,
            name: product?.name,
            sku: h.sku,
            price: product?.price?.latestPrice || 0,
          };
        })
        .filter((ah) => !!ah.price && !!ah.quantity);
    }, [hamperData, productHampers]) || [];

  const handleBuyNowHamper = async () => {
    try {
      // add item to cart
      const productsAddToCart: AddItemsProduct[] = [];
      // calculate hamper build sẵn

      const skuAvailableHamper = productHampers.map((p) => p.sku);

      const productHampersAddToCart = hamperData.hampers
        ? hamperData.hampers
            .filter(
              (p) => p.quantity !== 0 && !!skuAvailableHamper.includes(p.sku),
            ) // clear các sảm phẩm trong giỏ quà build sẵn mà không thuộc siêu thị hiện tại, và có số lượng = 0
            .map((item) => ({
              sku: item.sku,
              quantity: item.quantity,
              promotionId:
                productHampers.find((p) => p.sku === item.sku)
                  ?.selectPromotionId || undefined,
            }))
        : [];

      // calculate custom hampers
      const customHamperSkus: IHamperItemData[] = [];
      hamperData.configs
        .filter(
          (config) => !!config?.['quantity'] && config?.['quantity'] !== 0,
        ) // loại những config có số lượng = 0
        .forEach((hamperConfig) => {
          Object.keys(hamperConfig).forEach((configKey) => {
            const skus = hamperConfig[configKey];
            if (Array.isArray(skus)) {
              skus.forEach(({ sku, quantity }) => {
                customHamperSkus.push({
                  sku: sku,
                  quantity:
                    quantity * ((hamperConfig?.['quantity'] || 0) as number),
                });
              });
            }
          });
        });

      const skuHamperDetailProducts = hamperDetailProducts?.products?.map(
        (p) => p.sku,
      );
      const customHamperSkusAddToCart = customHamperSkus
        .filter(
          (h) => h.quantity !== 0 && !!skuHamperDetailProducts.includes(h.sku),
        ) // clear hết các sku không thuộc siêu thị hiện tại và có số lượng = 0
        .map((item) => ({
          sku: item.sku,
          quantity: item.quantity,
          promotionId:
            hamperDetailProducts.products?.find((p) => p.sku === item.sku)
              ?.selectPromotionId || undefined,
        }));
      productsAddToCart.push(
        ...productHampersAddToCart,
        ...customHamperSkusAddToCart,
      );
      await onAddMultipleItems(productsAddToCart, {
        isolatedCartType: IsolatedCartType.BUY_NOW,
        isSkipHoldInventory: true,
      });
      // in app
      if (TerraKit.isAvailable()) {
        const cartToken = getHamperDataLocalConfigs().cartToken;
        if (cartToken) {
          const products = customHamperSkusAddToCart.map((item) => ({
            ...item,
            name:
              hamperDetailProducts.products?.find((p) => p.sku === item.sku)
                ?.name || '',
          }));
          const note = buildHamperNote(hamperData, products);
          observeCheckoutHamperResult({
            token: cartToken || '',
            note,
            cartType: CartType.GIFT_BASKET,
          });
        } else {
          toast?.error?.({
            title: t('Error'),
            message: t('Không lấy được cart token. Vui lòng thử lại!'),
          });
        }
      } else {
        // Redirect to checkout page
        const key = getBuyNowKey(true);
        router.push(`/checkout?bnk=${key}&isFromBom=true`);
      }
    } catch (error) {
      logger.log(error, 'Add hamper to cart failed');
    }
  };

  return (
    <HamperContext.Provider
      value={{
        hamperData,
        currentHamperConfig,
        changeActiveConfig,
        updateCurrentConfig,
        onUpdateConfig,
        hamperDetailProducts: hamperDetailProducts,
        loading: loading || isLoading,
        increaseHamperItemQuantity,
        decreaseHamperItemQuantity,
        removeHamperItem,
        setHamperItemQuantity,
        increaseCurrentHamperQuantity,
        decreaseCurrentHamperQuantity,
        setCurrentHamperQuantity,
        addHamper,
        onChangeQuantityHamper,
        minusHamper,
        removeHamper,
        productHampers,
        setProductHampers,
        priceSummary,
        priceAvailableHamper,
        allPriceSummary,
        handleBuyNowHamper,
        availableHamper,
      }}
    >
      {children}
    </HamperContext.Provider>
  );
};

export const useHamper = () => {
  const context = useContext(HamperContext);
  if (!context) {
    throw new Error(
      'useHamper must be inside a HamperProvider with a state value',
    );
  }
  return context;
};
